import { isDevelopment } from "../common/helpers/env";
import React from "react";
import { createRoot, hydrateRoot, Root } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Provider } from "react-redux";
import { createStore, applyMiddleware, compose } from "redux";
import ReactGA from "react-ga4";
import { withExtraArgument } from "redux-thunk";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { IntlProvider } from "react-intl";

import "../common/helpers/requestAnimFrame";

import reducers from "./store";
import routes from "./routes";
import AppRoutes from "./AppRoutes";
import { routesMapper, IRoute, preloadRoutes } from "../common/helpers/routesMapper";
import { preloadPages } from "../common/helpers/lazyPage";
import { getItem, setItem, hasItem } from "./cookie";
import getLanguage from "./utils/getLanguage";
import { availableLanguages } from "../common/parameters";
import NotSupported from "./components/NotSupported";
import isSupportedBrowser from "./utils/isSupportedBrowser";
import { IUser } from "./store/user/models";
import { tryReloadOnChunkLoadError } from "./utils/chunkLoadError";
import { StyleSheetManager } from "styled-components";
import shouldForwardProp from "../common/helpers/shouldForwardProp";

if (!isDevelopment && isSupportedBrowser().isSupported === "yes") {
    Sentry.init({ dsn: "https://d856ee956efc4922bdb64022a731b2f8@sentry.io/1501725" });
} else if (!isDevelopment) {
    console.warn("Not supported", isSupportedBrowser().supportMessage);
}

const isAdmin =
    isDevelopment ||
    (window.INITIAL_STATE.user && (window.INITIAL_STATE.user as IUser)._id === "5b2504ca09ddd4002a1d40f3");
if (!isAdmin) {
    if (window.location.host.indexOf(".com") !== -1) {
        ReactGA.initialize("G-SDZCK6SZE2");
        ReactGA.send({ hitType: "pageview", page: window.location.pathname });
    }
}

window.historyList = [];
const routeObjects = routesMapper(routes as IRoute[]);

const axiosInstance = axios.create({
    baseURL: "/api",
});

const axiosRoot = axios.create({
    baseURL: "",
});

let analyticsEnabled = true;
const doAnalytics = async () => {
    if (!analyticsEnabled) return;
    analyticsEnabled = false;
    if (hasItem("aid")) {
        const _aid = getItem("aid");
        const { data } = await axiosInstance.post("/user-analytics", { _aid });
        if (data !== "success") {
            setItem("aid", data, Infinity);
        }
    } else {
        const { data: _aid } = await axiosInstance.post("/user-analytics", { _aid: null });
        setItem("aid", _aid, Infinity);
    }
    setTimeout(() => {
        analyticsEnabled = true;
    }, 5 * 60 * 1000);
};

setTimeout(doAnalytics, 3000);

// The server picked the language and wrote it into <html lang>. Working it out here from
// the cookie or navigator.language again can land on a different answer, and then the
// first render would be a differently translated tree than the markup it is hydrating.
const documentLanguage = document.documentElement.lang;
let language = availableLanguages.includes(documentLanguage) ? documentLanguage : getLanguage();

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
const store = createStore(
    reducers(),
    window.INITIAL_STATE,
    composeEnhancers(applyMiddleware(withExtraArgument(axiosInstance)))
);
window.store = store;

const container = document.getElementById("root")!;

/**
 * Whether there is a page to take over is the document's question to answer, not a build
 * flag's. The flag this replaced was true in every client build, development and
 * production alike, so the browser always took the "draw it from scratch" branch and
 * threw away the page the server had just sent.
 */
const serverRendered = !!container.firstChild;

/**
 * The root is made once and kept. A language change renders into it again, and a
 * container can only be given one root - calling createRoot on it twice is an error.
 */
let root: Root | undefined;

const renderInto = (element: React.ReactElement, takeOver = serverRendered) => {
    if (root) {
        root.render(element);
        return;
    }
    if (takeOver) {
        root = hydrateRoot(container, element);
        return;
    }
    // Whatever the server sent is replaced rather than taken over.
    container.innerHTML = "";
    root = createRoot(container);
    root.render(element);
};

const renderPageWithLanguage = (language: string) => {
    setItem("language", language, 60 * 60 * 24 * 365);
    document.getElementsByTagName("html")[0].setAttribute("lang", language);
    const { isSupported, supportMessage } = isSupportedBrowser();
    window.recaptchaOptions = { lang: language, removeOnMount: true };
    if (isSupported === "no") {
        // Not a hydration: the notice replaces the page the server rendered.
        renderInto(
            <IntlProvider locale={language} key={language}>
                <NotSupported notSupported={supportMessage} />
            </IntlProvider>,
            false
        );
    } else {
        const timeago = window.registerLanguageForTimeago(language);
        const languages = axiosRoot.get("/translations/" + language + ".json");
        Promise.all([languages, timeago]).then(([res]) => {
            let messages = res.data;
            const renderFunc = () => {
                renderInto(
                        <Provider store={store}>
                            <IntlProvider locale={language} messages={messages}>
                                <BrowserRouter>
                                    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                                        <AppRoutes routes={routeObjects} isAdmin={isAdmin} onRouteChange={doAnalytics} seo={window.ROUTE_SEO} />
                                    </StyleSheetManager>
                                </BrowserRouter>
                            </IntlProvider>
                        </Provider>
                );
            };
            // What loadableReady() used to guarantee: the matched pages are in memory
            // before hydration, so the client's first render matches the server's markup.
            // The route's own pages first, then the ones the server reports having
            // rendered inside them - those are only reachable once their page is loaded.
            preloadRoutes(routeObjects, window.location.pathname)
                .then(() => preloadPages(window.RENDERED_PAGES || []))
                .then(renderFunc, renderFunc);
        });
    }
};

window.renderPageWithLanguage = renderPageWithLanguage;

renderPageWithLanguage(language);

document.addEventListener("dragstart", function (event: DragEvent) {
    if ((event.target as Element).nodeName.toUpperCase() === "IMG") {
        event.preventDefault();
    }
});

if ("serviceWorker" in navigator) {
    navigator.serviceWorker.register("/service-worker.js");
}

window.addEventListener("unhandledrejection", event => {
    if (tryReloadOnChunkLoadError(event.reason)) {
        event.preventDefault();
    }
});
